import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip"
import type { ComponentProps, ReactElement, ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * Themed Tooltip on Base UI — used for the collapsed sidebar rail's icon-only rows.
 * The floating label is a small OPAQUE chip (bg-popover + hairline ring + shadow, the
 * house overlay recipe) — translucent `.glass` chips read as a rendering bug over text.
 * Placement is dir-aware: callers pass a logical side (`inline-end` = toward content
 * in both LTR and RTL). Wrap a subtree in `TooltipProvider` for grouped instant re-open.
 */

export function TooltipProvider({
  delayDuration,
  delay,
  ...props
}: ComponentProps<typeof BaseTooltip.Provider> & { delayDuration?: number }) {
  return <BaseTooltip.Provider delay={delayDuration ?? delay} {...props} />
}

type Side = ComponentProps<typeof BaseTooltip.Positioner>["side"]

export function Tooltip({
  label,
  content,
  className,
  side = "inline-end",
  sideOffset = 8,
  children,
}: {
  label?: ReactNode
  content?: ReactNode
  className?: string
  side?: Side
  sideOffset?: number
  /** The trigger element (a button/link); tooltip props are merged onto it. */
  children: ReactElement
}) {
  return (
    <BaseTooltip.Root>
      {/* Base UI's `render` merges the tooltip's trigger props onto the caller's element
          (a Link/button/anchor). It types the payload as Record<string, unknown>; our
          concrete trigger elements satisfy that at the call site — the cast only bridges
          the generic wrapper, it doesn't loosen anything the caller passes. */}
      <BaseTooltip.Trigger render={children as ReactElement<Record<string, unknown>>} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} sideOffset={sideOffset}>
          <BaseTooltip.Popup
            className={cn(
              "z-50 rounded-md bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md ring-1 ring-foreground/5 outline-none dark:ring-foreground/10",
              "origin-[var(--transform-origin)] animate-in fade-in zoom-in-95",
              className,
            )}
          >
            {label ?? content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
